<template>
  <div :class="['device-selection virtual-bg', { loading: loading }]">
    <div v-for="(item, idx) in bgOptions" :key="idx" :class="{ disabled : disabledSelection || tempDisabled }">
      <div :class="['icon', { active: virtualBg === item.value }]" @click="selectVirtualBg(item.value)">
        <img :src="item.icon" />
      </div>
    </div>
    <!-- Fallback path: Shows video with virtual background (BodyPix canvas) -->
    <canvas v-if="needsCanvasElements" class="virtual-bg-video"></canvas>
    <!-- Fallback path: Original video to be processed for blur effect -->
    <video v-if="needsCanvasElements" class="for-process-video" muted="muted" autoplay playsinline />
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import { store } from '@/utils/common';
import virtualBgMixin from '@/mixins/virtualBg';
import { vbExtension } from '@/modules/webrtc/agora/properties/initVirtualBackground';

export default {
  mixins: [virtualBgMixin],
  data() {
    return {
      loading: false,
      agoraVbExtensionSupported: false,
      webgl2Support: true,
      hardwareAcceleration: true
    };
  },
  created() {
    const videoEffect = store.getBlurState();
    this.setVirtualBg(videoEffect);

    // Only check Agora VB extension compatibility for Agora sessions
    if (this.provider === 'agora') {
      this.agoraVbExtensionSupported = vbExtension.checkCompatibility();
    }
  },
  mounted() {
    // webgl v2 is recommended for smooth blur effect
    this.webgl2Support = !!document.createElement('canvas').getContext('webgl2');
  },
  watch: {
    isLessonEnded(state) {
      if (state && this.virtualBgModel) {
        // Release BodyPix model memory when lesson ends
        // (used by SkyWay, Agora fallback path, and any session that loaded BodyPix)
        this.virtualBgModel.releaseMemory();
      }
    },
    disabledSelection(val) {
      if (val) {
        this.logBlurDisabledReason();
        this.setVirtualBg('none');
        store.setBlurState('none');
      }
    }
  },
  computed: {
    ...mapState(['lessonId', 'provider']),
    ...mapState('videoRtc', ['rtcClient', 'localUser', 'virtualBg', 'virtualBgModel', 'isShareScreenOn']),
    ...mapGetters(['isLessonEnded', 'isWaitingForLesson', 'isAmazonChime']),

    tempDisabled() {
      return this.isLessonEnded || this.isShareScreenOn;
    },

    disabledSelection() {
      // No camera always disables blur, regardless of provider.
      if (this.noCamera) {
        return true;
      }

      // Agora-only relaxation: when the native VB extension is available, blur
      // runs on WASM + the extension's own rendering, so the WebGL2/hardware
      // gates don't apply. (The extension's checkCompatibility() >= 94 fast
      // path only checks that WebGL2RenderingContext exists on the global, not
      // that getContext('webgl2') succeeds — so gating on the live check would
      // hide blur from compatible users.)
      if (this.provider === 'agora' && this.agoraVbExtensionSupported) {
        return false;
      }

      // All other providers keep the original WebGL2/hardware gate.
      return !this.webgl2Support || !this.hardwareAcceleration;
    },

    noCamera() {
      return this.localUser.stream && !this.localUser.stream.getVideoTracks().length;
    },

    // Canvas/video elements are needed for BodyPix pipeline:
    // - SkyWay always uses canvas pipeline
    // - Agora uses canvas pipeline only when extension is not supported
    // - Chime never needs it (uses BackgroundBlurVideoFrameProcessor)
    needsCanvasElements() {
      if (this.isAmazonChime) return false;
      if (this.provider === 'agora' && this.agoraVbExtensionSupported) return false;
      return true;
    },

    bgOptions() {
      return [
        {
          icon: require('@/assets/img/blur_icn-off.svg'),
          value: 'none'
        },
        {
          icon: require('@/assets/img/blur_icn-on.svg'),
          value: 'blur'
        }
      ];
    }
  },
  methods: {
    ...mapMutations('videoRtc', ['setVirtualBg', 'setVirtualBgModel']),
    ...mapActions('videoRtc', ['updateLocalStream']),

    selectVirtualBg(newEffect) {
      if (this.virtualBg === newEffect || this.disabledSelection || this.tempDisabled) {
        return;
      }

      if (this.isWaitingForLesson) {
        this.setVirtualBg(newEffect);
        store.setBlurState(newEffect);
        return;
      }

      this.applyEffect(newEffect);
    },

    ignoreBackgroundEffect() {
      return this.loading || !this.localUser.stream;
    },

    async applyEffect(effect) {
      if (this.ignoreBackgroundEffect()) {
        return;
      }
      this.loading = true;

      this.hardwareAcceleration = await this.loadVBModel();

      if (!this.hardwareAcceleration) {
        this.loading = false;
        return;
      }

      // Perform Video Process and Publish Effect to Stream
      const rtc = await this.rtcClient.getAdapter().setVideoEffect(effect, this.virtualBgModel);
      this.updateLocalStream(rtc);

      this.setVirtualBg(effect);
      store.setBlurState(effect);

      // add delay to prevent republishing stream immediately
      setTimeout(() => {
        this.loading = false;
      }, 1000);
    },

    logBlurDisabledReason() {
      // For Agora with the native extension, WebGL2/hardware are not gating
      // factors, so don't report them as the reason.
      const agoraExtensionActive = this.provider === 'agora' && this.agoraVbExtensionSupported;

      let reason;
      if (!agoraExtensionActive && !this.webgl2Support) {
        reason = 'WebGL is not supported';
      } else if (!agoraExtensionActive && !this.hardwareAcceleration) {
        reason = 'Hardware Acceleration is disabled';
      } else if (this.isShareScreenOn) {
        reason = 'Share screen is currently active';
      } else if (this.noCamera) {
        reason = 'Cannot access camera';
      }
      this.$logger.info('Blur is disabled', { operation: 'blur:disabled', reason: reason });
    }
  }
};
</script>

<style scoped src="@/assets/css/pc/side_menu.css"></style>
